import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { debounce } from "lodash";
import { toast } from "react-toastify";

// Services & Types
import { TitheService } from "services/tithe.service";
import { ITitheResponseDTO } from "models/church.types";

export default function TitheList() {
  const [tithes, setTithes] = useState<ITitheResponseDTO[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  // Total cumulé de la page courante
  const totalAmount = tithes.reduce((acc, curr) => acc + (curr.amount || 0), 0);

  // Calcul du nombre total de pages
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const loadData = async (query: string, page: number) => {
    setLoading(true);
    try {
      let result;
      if (query.trim()) {
        result = await TitheService.search(query, page, itemsPerPage);
      } else {
        result = await TitheService.getAll(page, itemsPerPage);
      }

      setTithes(result.data || []);
      setTotalItems(result.totalCount || 0);
    } catch (error) {
      console.error("Erreur de chargement des dîmes:", error);
      toast.error("Impossible de charger le registre des dîmes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(searchTerm, currentPage);
  }, [currentPage]);

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setCurrentPage(0);
      loadData(value, 0);
    }, 500),
    []
  );

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm("Voulez-vous vraiment supprimer cet enregistrement de dîme ?")) {
      setLoading(true);
      try {
        await TitheService.delete(id);
        toast.success("Dîme supprimée avec succès");
        loadData(searchTerm, currentPage);
      } catch (error) {
        toast.error("Erreur lors de la suppression de la dîme.");
      } finally {
        setLoading(false);
      }
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "XOF",
    }).format(amount);
  };

  return (
    <div className="flex flex-col w-full px-6 min-h-screen">
      {/* HEADER SECTION */}
      <div className="mb-6 pt-8">
        <h2 className="text-white text-3xl font-bold uppercase tracking-tight flex items-center gap-3">
          <i className="fas fa-donate text-[#D4AF37]"></i> Registre des Dîmes
        </h2>
        <p className="text-slate-400 text-sm italic">
          Suivi et enregistrement des dîmes versées au secrétariat de l'église
        </p>
      </div>

      {/* BARRE DE RECHERCHE ET ACTIONS */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-2 w-full md:w-auto">
          <div className="relative w-full md:w-96 group">
            <span className="absolute inset-y-0 left-0 pl-4 flex items-center text-slate-400 group-focus-within:text-[#D4AF37] transition-colors">
              {loading ? (
                <i className="fas fa-circle-notch animate-spin text-[#D4AF37]"></i>
              ) : (
                <i className="fas fa-search"></i>
              )}
            </span>
            <input
              type="text"
              value={searchTerm}
              onChange={onSearchChange}
              placeholder="Rechercher une dîme..."
              className="w-full bg-slate-800 border border-slate-700 text-white text-sm rounded-lg pl-11 pr-4 py-2 font-sans h-[38px] outline-none focus:border-[#D4AF37] transition-colors"
            />
          </div>
        </div>

        <Link
          to="/admin/tithes/register"
          className="bg-[#D4AF37] hover:bg-[#b8952e] text-slate-900 font-bold px-8 h-[38px] flex items-center justify-center rounded-lg whitespace-nowrap transition-colors w-full lg:w-auto text-center text-sm shadow-md hover:shadow-lg"
        >
          <i className="fas fa-plus mr-2"></i> Enregistrer une dîme
        </Link>
      </div>

      {/* TABLE CARD */}
      <div className="bg-slate-800 border border-slate-700 rounded-2xl overflow-hidden shadow-2xl mb-10">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-[#1E293B] text-slate-300 uppercase text-[10px] font-black tracking-widest border-b border-slate-700">
                <th className="px-6 py-5">ID</th>
                <th className="px-6 py-5 text-center">Date</th>
                <th className="px-6 py-5">Description / Note</th>
                <th className="px-6 py-5">Caisse</th>
                <th className="px-6 py-5 text-right">Montant</th>
                <th className="px-6 py-5 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50 text-slate-300">
              {tithes.length === 0 && !loading ? (
                <tr>
                  <td
                    colSpan={6}
                    className="text-center py-16 text-slate-500 italic text-sm"
                  >
                    <i className="fas fa-hand-holding-heart block text-3xl mb-3 opacity-20"></i>
                    Aucune dîme trouvée dans le registre.
                  </td>
                </tr>
              ) : (
                tithes.map((tithe) => (
                  <tr
                    key={tithe.id}
                    className="hover:bg-slate-700/40 transition-colors group"
                  >
                    <td className="px-6 py-4 text-xs font-bold text-[#D4AF37]">
                      #{tithe.id}
                    </td>
                    <td className="px-6 py-4 text-xs text-slate-400 text-center whitespace-nowrap">
                      {tithe.titheDate
                        ? new Date(tithe.titheDate).toLocaleDateString("fr-FR", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                          })
                        : "-"}
                    </td>
                    <td className="px-6 py-4 text-xs text-slate-300 max-w-xs truncate">
                      {tithe.description || (
                        <span className="text-slate-600 italic">Aucune description</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {tithe.fundName ? (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-slate-900 text-[#D4AF37] border border-slate-700 shadow-sm">
                          <i className="fas fa-wallet mr-1.5 text-[10px]"></i>
                          {tithe.fundName}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-700/50 text-slate-400 border border-slate-600/40">
                          <i className="fas fa-exclamation-circle mr-1.5 text-[10px] text-amber-500/70"></i>
                          Non rattachée
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right font-black text-emerald-400 whitespace-nowrap text-sm">
                      {formatCurrency(tithe.amount)}
                    </td>
                    <td className="px-6 py-4 text-center whitespace-nowrap">
                      <div className="flex justify-center gap-4">
                        <Link
                          to={`/admin/tithes/edit/${tithe.id}`}
                          className="text-slate-400 hover:text-[#D4AF37] transition-colors"
                          title="Modifier"
                        >
                          <i className="fas fa-edit text-base"></i>
                        </Link>
                        <button
                          onClick={() => handleDelete(tithe.id!)}
                          className="text-slate-400 hover:text-red-500 transition-colors"
                          title="Supprimer"
                        >
                          <i className="fas fa-trash-alt text-base"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            <tfoot className="bg-[#1E293B] border-t border-slate-700">
              <tr>
                <td
                  colSpan={4}
                  className="px-6 py-4 text-right font-black text-slate-300 uppercase text-[10px] tracking-wider"
                >
                  Total Page :
                </td>
                <td className="px-6 py-4 text-right font-black text-[#D4AF37] text-sm">
                  {formatCurrency(totalAmount)}
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* SECTION PAGINATION */}
        <div className="px-6 py-5 bg-[#0F172A]/50 border-t border-slate-700 flex items-center justify-between">
          <p className="text-[11px] text-slate-500 font-bold uppercase tracking-wider">
            Total global : <span className="text-[#D4AF37]">{totalItems}</span>{" "}
            dîmes
          </p>
          <div className="flex gap-2">
            <button
              disabled={currentPage === 0}
              onClick={() => setCurrentPage((p) => p - 1)}
              className="px-5 py-2 text-[10px] font-black uppercase rounded-lg bg-slate-900 text-white border border-slate-700 disabled:opacity-20 hover:bg-slate-700 transition-all shadow-md"
            >
              Précédent
            </button>
            <button
              disabled={currentPage + 1 >= totalPages || totalPages === 0}
              onClick={() => setCurrentPage((p) => p + 1)}
              className="px-5 py-2 text-[10px] font-black uppercase rounded-lg bg-slate-900 text-white border border-slate-700 disabled:opacity-20 hover:bg-slate-700 transition-all shadow-md"
            >
              Suivant
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}